import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useClientConfig } from "../hooks/useClientConfig";
import { debugLog } from "../utils/debug";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const config = useClientConfig();

  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [emAtendimento, setEmAtendimento] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const clientId = searchParams.get("clientId") || "";
  const clientName = searchParams.get("clientName") || "";

  const transData = Object.fromEntries(
    [...searchParams.entries()].filter(
      ([key]) => key !== "clientId" && key !== "clientName"
    )
  );

  const transDataWithClient = {
    ...transData,
    ...(clientId ? { clientId } : {}),
    ...(clientName ? { clientName } : {}),
  };

  useEffect(() => {
    localStorage.setItem("chatMessages", JSON.stringify(messages));
    debugLog("💾 Salvando mensagens no localStorage:", messages);
  }, [messages]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const filtered = messages.filter((msg) => now - msg.timestamp < 24 * 60 * 60 * 1000);
      debugLog("⏳ Limpando mensagens antigas. Total atual:", messages.length, "Após limpeza:", filtered.length);
      setMessages(filtered);
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [messages]);

  useEffect(() => {
    if (!config || !config.wsUrl) return;

    if (!clientId || !clientName) {
      toast.error("❌ clientId ou clientName ausentes na URL. Conexão não iniciada.");
      return;
    }

    const connectionDelay = 1500;
    const wsUrl =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
        ? "ws://localhost:3001/ws"
        : config.wsUrl;

    let ws;

    const timer = setTimeout(() => {
      debugLog("🌐 Iniciando conexão com WebSocket:", wsUrl);
      ws = new WebSocket(wsUrl);
      setSocket(ws);

      ws.onopen = () => {
        debugLog("✅ WebSocket conectado com sucesso!");
        ws.send(JSON.stringify({ clientId, clientName, transData: transDataWithClient }));
      };

      ws.onmessage = (event) => {
        try {
          const response = JSON.parse(event.data);
          const now = Date.now();
          const mensagens = [];

          if (Array.isArray(response?.listaMessages)) {
            mensagens.push(...response.listaMessages);
          }

          if (response?.type === "new_messages" && Array.isArray(response.data?.downlinkMessages)) {
            mensagens.push(...response.data.downlinkMessages);
          }

          debugLog("📥 Mensagens recebidas:", mensagens);

          const novas = mensagens.map((msg) => ({
            from:
              msg.sourceType === "AGENT" ? "agent" :
                msg.sourceType === "USER" ? "user" :
                  msg.sourceType === "ROBOT" ? "robot" :
                    "system",
            text: msg.content,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            timestamp: now,
            sourceType: msg.sourceType || null,
            controlType: msg.controlType || null,
            senderNickname: msg.senderNickname || null,
            senderAvatar: msg.senderAvatar || null,
            mediaType: msg.mediaType || null,
            contentType: msg.contentType || null,
            mediaFileType: msg.mediaFileType || null,
            showSurvey: msg.showSurvey || false,
          }));

          setMessages((prev) => {
            const existentes = new Set(prev.map((m) => `${m.text}-${m.timestamp}-${m.senderNickname}`));
            return [...prev, ...novas.filter((m) => !existentes.has(`${m.text}-${m.timestamp}-${m.senderNickname}`))];
          });

          const ultima = novas.at(-1);
          if (ultima?.showSurvey) {
            setShowEndModal(true);
            debugLog("📊 Ativando modal de avaliação.");
          }

          if (response?.messageCode === "connected" && response.clientId) {
            setAgentInfo({ to: response.clientId });
            debugLog("📡 Agente conectado:", response.clientId);
          }

          if (response?.emAtendimento) {
            setEmAtendimento(response.emAtendimento);
            debugLog("👤 Em atendimento:", response.emAtendimento);
          }

        } catch (err) {
          console.error("❌ Erro ao processar mensagem:", err);
          toast.error("❌ Erro ao processar mensagem.");
        }
      };

      ws.onerror = (err) => {
        console.error("❌ Erro no WebSocket:", err);
        toast.error("❌ Erro no WebSocket.");
      };

      ws.onclose = (event) => {
        try {
          if (messages.length > 0) {
            const sessionHistories = JSON.parse(localStorage.getItem("sessionHistories") || "[]");
            sessionHistories.push({ timestamp: Date.now(), messages });
            localStorage.setItem("sessionHistories", JSON.stringify(sessionHistories));
            debugLog("📁 Sessão salva no histórico.");
          }
        } catch (e) {
          console.error("❌ Erro ao salvar sessão anterior:", e);
        }

        toast.warn(`🔌 ${event.reason || "WebSocket desconectado"}`);
      };
    }, connectionDelay);

    return () => {
      clearTimeout(timer);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
        debugLog("🧹 WebSocket encerrado.");
      }
    };
  }, [config]);

  const sendPayload = (type, payloadData = {}) => {
    if (socket?.readyState === WebSocket.OPEN) {
      const finalPayload = {
        type,
        data: { ...payloadData, clientId, clientName, transData: transDataWithClient },
      };
      debugLog("📤 Enviando payload:", finalPayload);
      socket.send(JSON.stringify(finalPayload));
    } else {
      toast.warn("🚫 WebSocket não está conectado.");
    }
  };

  if (!config) return null;

  return (
    <WebSocketContext.Provider
      value={{
        socket,
        messages,
        setMessages,
        agentInfo,
        showEndModal,
        setShowEndModal,
        sendPayload,
        transData,
        clientId,
        clientName,
        emAtendimento,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
